import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Loader2 } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import SectionHeader from '@/components/common/SectionHeader';
import apiService from '@/services/api';

interface Region {
  id: number;
  name: string;
  description?: string;
  postcode_prefix?: string;
  coverage_area?: string;
}

const LocationsPage = () => {
  const [regions, setRegions] = useState<Region[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadRegions = async () => {
      try {
        setLoading(true);
        // Try to fetch regions from the API
        const response = await apiService.get('/public/regions');
        if (response?.data && Array.isArray(response.data)) {
          setRegions(response.data);
        } else {
          // Fallback to common UK regions
          setRegions([
            { id: 1, name: 'London', description: 'Greater London area', postcode_prefix: 'E, N, NW, SE, SW, W, WC' },
            { id: 2, name: 'Greater Manchester', description: 'Manchester and surrounding areas', postcode_prefix: 'M' },
            { id: 3, name: 'East Midlands', description: 'Nottingham, Derby, Leicester', postcode_prefix: 'NG, DE, LE' },
            { id: 4, name: 'West Midlands', description: 'Birmingham, Coventry, Wolverhampton', postcode_prefix: 'B, CV, WV' },
            { id: 5, name: 'Yorkshire', description: 'Leeds, Sheffield, Bradford', postcode_prefix: 'LS, S, BD' },
            { id: 6, name: 'South East', description: 'Kent, Sussex, Surrey', postcode_prefix: 'CT, TN, BN, RH, GU' },
          ]);
        }
      } catch (err) {
        console.error('Failed to fetch regions:', err);
        // Use fallback data on error
        setRegions([
          { id: 1, name: 'London', description: 'Greater London area' },
          { id: 2, name: 'Greater Manchester', description: 'Manchester and surrounding areas' },
          { id: 3, name: 'East Midlands', description: 'Nottingham, Derby, Leicester' },
          { id: 4, name: 'West Midlands', description: 'Birmingham, Coventry, Wolverhampton' },
        ]);
      } finally {
        setLoading(false);
      }
    };

    loadRegions();
  }, []);

  return (
    <Layout>
      <Helmet>
        <title>Service Locations | Virewire Property Services</title>
        <meta name="description" content="Virewire provides property maintenance and cleaning services across London, Manchester, East Midlands, and more UK regions." />
        <meta property="og:title" content="Service Locations | Virewire Property Services" />
        <meta property="og:description" content="Find Virewire property services in your region. We cover London, Manchester, Nottingham and more." />
        <link rel="canonical" href="https://virewire.com/locations" />
      </Helmet>

      {/* Hero Section */}
      <section className="bg-primary text-white py-16 lg:py-24">
        <div className="container-custom">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
            We Cover You
          </h1>
          <p className="text-xl text-white/90 max-w-2xl leading-relaxed">
            Professional property services across the UK. Find local coverage in your area.
          </p>
        </div>
      </section>

      {/* Regions Grid */}
      <section className="section-padding bg-soft-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center items-center min-h-[400px]">
              <Loader2 className="w-12 h-12 animate-spin text-primary" />
            </div>
          ) : error ? (
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg text-center">
              {error}
            </div>
          ) : regions.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {regions.map((region) => (
                <div 
                  key={region.id}
                  className="bg-white rounded-2xl p-8 shadow-card hover:shadow-card-lg transition-all duration-300 border border-border group"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-lg bg-lime-100 flex items-center justify-center group-hover:bg-lime-500 transition-colors">
                      <MapPin className="h-6 w-6 text-primary group-hover:text-white transition-colors" />
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-primary mb-3">
                    {region.name}
                  </h3>
                  <p className="text-text-secondary mb-4 leading-relaxed">
                    {region.description || 'Professional property services in this region.'}
                  </p>
                  {region.postcode_prefix && (
                    <p className="text-sm text-text-secondary mb-6 bg-secondary p-3 rounded-lg">
                      <span className="font-semibold">Postcodes: </span>{region.postcode_prefix}
                    </p>
                  )}
                  <Button variant="outline" asChild className="w-full">
                    <Link to={`/quote?region=${region.id}`}>
                      Get Quote <ArrowRight className="h-4 w-4 ml-2" />
                    </Link>
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 text-text-secondary">
              <p className="text-lg">No locations available at this time.</p>
            </div>
          )}
        </div>
      </section>

      {/* Coverage Info */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <SectionHeader 
            title="Nationwide Coverage"
            subtitle="Expanding across the UK"
            centered
          />
          <div className="grid md:grid-cols-2 gap-12 items-center mt-12">
            <div>
              <h3 className="text-2xl font-bold text-primary mb-6">
                Currently Serving
              </h3>
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-lime-500"></span>
                  <span className="text-text-primary">Full coverage in London</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-lime-500"></span>
                  <span className="text-text-primary">Major regional centers</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-lime-500"></span>
                  <span className="text-text-primary">Multi-location support</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="w-2 h-2 rounded-full bg-lime-500"></span>
                  <span className="text-text-primary">Growing network of partners</span>
                </li>
              </ul>
            </div>
            <div className="bg-secondary rounded-2xl p-8 lg:p-12">
              <h3 className="text-2xl font-bold text-primary mb-4">
                Don't See Your Area?
              </h3>
              <p className="text-text-secondary mb-6 leading-relaxed">
                We're constantly expanding our network. Get in touch to find out if we can serve your location.
              </p>
              <Button variant="default" asChild>
                <Link to="/contact">
                  Contact Us <ArrowRight className="h-4 w-4 ml-2" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-primary text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Get Instant Quote in Your Area
          </h2>
          <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
            Enter your postcode to check availability and get an instant quote.
          </p>
          <Button variant="secondary" size="lg" asChild>
            <Link to="/service">
              Get Instant Quote <ArrowRight className="h-5 w-5 ml-2" />
            </Link>
          </Button>
        </div>
      </section>
    </Layout>
  );
};

export default LocationsPage;
